import React, { useState, useEffect } from 'react';
import { Gem, Watch, Zap, Download } from 'lucide-react';
import { dashboardApi, backupApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
    relics: { icon: Gem, color: 'cyan', labelKey: 'relics' },
    accessories: { icon: Watch, color: 'amber', labelKey: 'accessories' },
    skills: { icon: Zap, color: 'purple', labelKey: 'skills' },
};

const ProgressRing = ({ percentage, color, size = 80 }) => {
    const strokeWidth = size > 60 ? 6 : 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;
    
    const fontSize = size >= 100 ? 'text-xl' : size >= 70 ? 'text-base' : 'text-sm';
    
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg className="transform -rotate-90" width={size} height={size}>
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-white/10 dark:text-white/10 light:text-gray-200" />
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                    className={cn("transition-all duration-1000 ease-out", color === 'cyan' && "text-cyan-400", color === 'amber' && "text-amber-400", color === 'purple' && "text-purple-400")} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className={cn("font-bold text-white dark:text-white light:text-gray-900", fontSize)}>{percentage}%</span>
            </div>
        </div>
    );
};

export const ProgressDashboard = () => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const loadProgress = async () => {
            if (!user?.discord_id) return;
            try {
                const response = await dashboardApi.getProgress(user.discord_id);
                setProgress(response.data);
            } catch (error) {
                console.error('Failed to load progress:', error);
            } finally {
                setLoading(false);
            }
        };
        loadProgress();
    }, [user?.discord_id]);

    const handleExport = async () => {
        if (!user?.discord_id) return;
        setExporting(true);
        try {
            const response = await backupApi.exportUserProgress(user.discord_id);
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `progress-export-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(t('exportProgress') + '!');
        } catch (error) {
            toast.error(t('error'));
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
    if (!progress) return <div className="p-8 text-center text-gray-500">{t('noResults')}</div>;

    // Calculate overall progress only for the 3 categories
    const totalReceived = (progress.relics?.received || 0) + (progress.accessories?.received || 0) + (progress.skills?.received || 0);
    const totalItems = (progress.relics?.total || 0) + (progress.accessories?.total || 0) + (progress.skills?.total || 0);
    const overallPercentage = totalItems > 0 ? Math.round((totalReceived / totalItems) * 100 * 10) / 10 : 0;

    return (
        <div className="space-y-6" data-testid="progress-dashboard">
            <div className="p-6 rounded-xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/10 dark:border-white/10 light:border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-white dark:text-white light:text-gray-900 font-['Rajdhani']">{t('overallProgress')}</h3>
                        <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 text-sm mt-1">{t('progressDescription')}</p>
                    </div>
                    <ProgressRing percentage={overallPercentage} color="cyan" size={100} />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                    const data = progress[key] || { received: 0, total: 0, percentage: 0 };
                    const Icon = config.icon;
                    return (
                        <div key={key} data-testid={`progress-${key}`} className={cn("p-4 rounded-xl border transition-colors bg-card dark:bg-card light:bg-white hover:border-white/20 dark:hover:border-white/20 light:hover:border-gray-300", config.color === 'cyan' && "border-cyan-500/20 hover:border-cyan-500/40", config.color === 'amber' && "border-amber-500/20 hover:border-amber-500/40", config.color === 'purple' && "border-purple-500/20 hover:border-purple-500/40")}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className={cn("p-2 rounded-lg", config.color === 'cyan' && "bg-cyan-500/10", config.color === 'amber' && "bg-amber-500/10", config.color === 'purple' && "bg-purple-500/10")}>
                                    <Icon size={18} className={cn(config.color === 'cyan' && "text-cyan-400", config.color === 'amber' && "text-amber-400", config.color === 'purple' && "text-purple-400")} />
                                </div>
                                <span className="font-medium text-white dark:text-white light:text-gray-900">{t(config.labelKey)}</span>
                            </div>
                            <div className="flex items-end justify-between">
                                <div>
                                    <p className="text-2xl font-bold text-white dark:text-white light:text-gray-900 font-mono">{data.received}/{data.total}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500 light:text-gray-400">{t('collected')}</p>
                                </div>
                                <ProgressRing percentage={data.percentage} color={config.color} size={70} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
